// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ReleaseDownloads } from '../components/ReleaseDownloads';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  ArrowLeft, 
  Users, 
  MoreVertical, 
  Shield, 
  ShieldOff,
  Trash2,
  BookOpen,
  Wrench,
  Clock,
  Settings,
  KeyRound,
  Copy,
  ArrowUpAZ,
  ArrowDownAZ,
  SortAsc,
  SortDesc
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editPermissions, setEditPermissions] = useState([]);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [sortBy, setSortBy] = useState('name'); // 'name', 'email', 'date'
  const [sortOrder, setSortOrder] = useState('asc');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/select-area');
      return;
    }
    fetchUsers();
  }, [isAdmin, navigate, fetchUsers]);

  // Sort users
  const sortedUsers = [...users].sort((a, b) => {
    let comparison = 0;
    
    if (sortBy === 'name') {
      comparison = (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' });
    } else if (sortBy === 'email') {
      comparison = (a.email || '').localeCompare(b.email || '', 'pt-BR', { sensitivity: 'base' });
    } else if (sortBy === 'date') {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      comparison = dateA - dateB;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const handleEditPermissions = (user) => {
    setEditingUser(user);
    setEditPermissions([...user.permissions]);
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(`${API}/users/${editingUser.id}/permissions`, {
        permissions: editPermissions
      });
      toast.success('Permissões atualizadas!');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar permissões';
      toast.error(message);
    }
  };

  const togglePermission = (perm) => {
    if (editPermissions.includes(perm)) {
      setEditPermissions(editPermissions.filter(p => p !== perm));
    } else {
      setEditPermissions([...editPermissions, perm]);
    }
  };

  const handleToggleAdmin = async (user) => {
    const newStatus = !user.is_admin;
    const confirmMsg = newStatus 
      ? `Tornar ${user.name} administrador?`
      : `Remover privilégios de admin de ${user.name}?`;
    
    if (!window.confirm(confirmMsg)) return;

    try {
      await axios.put(`${API}/users/${user.id}/admin`, {
        is_admin: newStatus
      });
      toast.success(newStatus ? 'Administrador adicionado!' : 'Privilégios removidos!');
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar status';
      toast.error(message);
    }
  };

  const handleChangeRole = async (user, newRole) => {
    try {
      await axios.put(`${API}/users/${user.id}/role`, { role: newRole });
      toast.success('Papel atualizado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar papel');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Excluir usuário ${user.name}? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${user.id}`);
      toast.success('Usuário excluído!');
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir usuário';
      toast.error(message);
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Resetar a senha de ${user.name}? Uma nova senha temporária será gerada.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/users/${user.id}/reset-password`);
      setResetPasswordResult(response.data);
      toast.success('Senha resetada com sucesso!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao resetar senha';
      toast.error(message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/select-area')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-xl tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Gerenciar Usuários
                </h1>
                <p className="text-sm text-slate-500">Administração do sistema</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-6">
        <ReleaseDownloads />
        <Card data-testid="users-table-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Usuários ({users.length})
              </CardTitle>
              
              {/* Sort Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                    {sortBy === 'name' && 'Nome'}
                    {sortBy === 'email' && 'Email'}
                    {sortBy === 'date' && 'Data'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => { setSortBy('name'); setSortOrder('asc'); }}>
                    <ArrowUpAZ className="w-4 h-4 mr-2" />
                    Nome (A-Z)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy('name'); setSortOrder('desc'); }}>
                    <ArrowDownAZ className="w-4 h-4 mr-2" />
                    Nome (Z-A)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { setSortBy('email'); setSortOrder('asc'); }}>
                    <ArrowUpAZ className="w-4 h-4 mr-2" />
                    Email (A-Z)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortBy('date'); setSortOrder('desc'); }}>
                    <Clock className="w-4 h-4 mr-2" />
                    Mais recentes
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full spinner"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Permissões</TableHead>
                    <TableHead>Papel</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedUsers.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center">
                            <span className="text-white font-medium text-sm">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{user.name}</p>
                            <p className="text-sm text-slate-500">{user.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {user.permissions?.includes('congregacao') && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <BookOpen className="w-3 h-3 mr-1" />
                              Congregação
                            </Badge>
                          )}
                          {user.permissions?.includes('manutencao') && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <Wrench className="w-3 h-3 mr-1" />
                              Manutenção
                            </Badge>
                          )}
                          {(!user.permissions || user.permissions.length === 0) && (
                            <span className="text-sm text-slate-400">Nenhuma</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <select
                          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                          value={user.role || 'membro'}
                          onChange={(e) => handleChangeRole(user, e.target.value)}
                          data-testid={`role-select-${user.id}`}
                        >
                          <option value="admin">Admin</option>
                          <option value="servo_contas">Servo de Contas</option>
                          <option value="secretario">Secretário</option>
                          <option value="auditor">Auditor</option>
                          <option value="membro">Membro</option>
                        </select>
                      </TableCell>
                      <TableCell>
                        {user.is_admin ? (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                            <Shield className="w-3 h-3 mr-1" />
                            Admin
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">
                            Usuário
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(user.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-menu-${user.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => handleEditPermissions(user)}
                              data-testid={`edit-permissions-${user.id}`}
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              Editar Permissões
                            </DropdownMenuItem>
                            {user.id !== currentUser?.id && (
                              <>
                                <DropdownMenuItem 
                                  onClick={() => handleResetPassword(user)}
                                  data-testid={`reset-password-${user.id}`}
                                >
                                  <KeyRound className="w-4 h-4 mr-2" />
                                  Resetar Senha
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleToggleAdmin(user)}
                                  data-testid={`toggle-admin-${user.id}`}
                                >
                                  {user.is_admin ? (
                                    <>
                                      <ShieldOff className="w-4 h-4 mr-2" />
                                      Remover Admin
                                    </>
                                  ) : (
                                    <>
                                      <Shield className="w-4 h-4 mr-2" />
                                      Tornar Admin
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteUser(user)}
                                  className="text-red-600"
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Excluir Usuário
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit Permissions Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-permissions-modal">
          <DialogHeader>
            <DialogTitle>Editar Permissões</DialogTitle>
            <DialogDescription>
              Defina quais áreas {editingUser?.name} pode acessar
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-3 p-4 rounded-lg border border-slate-200 hover:bg-slate-50">
              <Checkbox
                id="perm-congregacao"
                checked={editPermissions.includes('congregacao')}
                onCheckedChange={() => togglePermission('congregacao')}
                data-testid="checkbox-congregacao"
              />
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-blue-700" />
                </div>
                <Label htmlFor="perm-congregacao" className="cursor-pointer">
                  <p className="font-medium">Contas da Congregação</p>
                  <p className="text-sm text-slate-500">Acesso aos documentos financeiros</p>
                </Label>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-4 rounded-lg border border-slate-200 hover:bg-slate-50">
              <Checkbox
                id="perm-manutencao"
                checked={editPermissions.includes('manutencao')}
                onCheckedChange={() => togglePermission('manutencao')}
                data-testid="checkbox-manutencao"
              />
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-green-700" />
                </div>
                <Label htmlFor="perm-manutencao" className="cursor-pointer">
                  <p className="font-medium">Contas da Manutenção</p>
                  <p className="text-sm text-slate-500">Acesso aos documentos de manutenção</p>
                </Label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSavePermissions}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-permissions-button"
            >
              Salvar Permissões
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Result Dialog */}
      <Dialog open={!!resetPasswordResult} onOpenChange={(open) => !open && setResetPasswordResult(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-result-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-green-600" />
              Senha Resetada
            </DialogTitle>
            <DialogDescription>
              Uma senha temporária foi gerada para o usuário
            </DialogDescription>
          </DialogHeader>
          
          {resetPasswordResult && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div>
                  <p className="text-sm text-slate-500">Usuário</p>
                  <p className="font-medium">{resetPasswordResult.user_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{resetPasswordResult.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Senha Temporária</p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded font-mono text-lg">
                      {resetPasswordResult.temporary_password}
                    </code>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(resetPasswordResult.temporary_password)}
                      data-testid="copy-password-button"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Importante:</strong> Informe esta senha ao usuário de forma segura. 
                  O usuário deverá trocar a senha no próximo acesso.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button 
              onClick={() => setResetPasswordResult(null)}
              className="bg-slate-900 hover:bg-slate-800"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
